import { Box, Button, Stack } from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useSelector } from "react-redux";
import { selectUserData, selectUserToken } from "../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import BrandDirectorySearch from "../BrandDirectorySearch";
import BrandsFilterItems from "../BrandsFilterItems";
import { openRunBrandModal } from "../utils/ensureRunProcessSupportData";
import { normalizeBrandStatus } from "../utils/helpersOfBrands";

interface Props {
  rows: any[];
  selectedRows: number[];
  selectedBrandNames: string[];
  setSelectedBrandNames: (value: string[]) => void;
  selectedStatus: string[] | null;
  setSelectedStatus: (value: string[] | null) => void;
  selectedAccountStatus: string[] | null;
  setSelectedAccountStatus: (value: string[] | null) => void;
  selectedAffiliateSystem: string[] | null;
  setSelectedAffiliateSystem: (value: string[] | null) => void;
  selectedCompany: string[] | null;
  setSelectedCompany: (value: string[] | null) => void;
  selectedWebsite: string[] | null;
  setSelectedWebsite: (value: string[] | null) => void;
  clearFilters: () => void;
}

// Reuses the exact "Run Processes" bulk-button pattern from
// BrandsFilterCard.tsx (legacy data_collection/brand_data module) — same
// setModalOpen({isOpen, data:[...]}) dispatch into brandConfigurationReportSlice,
// which RunBrandData.tsx already reads.
const AllBrandsFilterCard = ({
  rows,
  selectedRows,
  selectedBrandNames,
  setSelectedBrandNames,
  ...filterProps
}: Props) => {
  const response = useSelector(selectUserData);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const hasUpdateBrandDataPermission = CheckPermissions(
    response?.data?.products?.data_collection,
    "brands",
    "update_brand_data",
  );

  // Run Processes only makes sense for brands that are actually runnable.
  // "Synced" is the only runnable status — the same rule that decides whether
  // the per-row "Run Brand" quick action shows in BrandsQuickActions.tsx — so
  // any selected brand outside it disables the bulk button too, rather than
  // letting the wizard open for a brand it can't run.
  const hasNonRunnableSelection = selectedRows.some((brandId) => {
    const brand = rows.find((row: any) => row.brand_id === brandId);
    return brand && normalizeBrandStatus(brand.status) !== "synced";
  });

  const handleRunProcesses = () => {
    if (selectedRows.length === 0) return;
    const selectedBrandData = selectedRows.map((brandId) => {
      const brand = rows.find((row: any) => row.brand_id === brandId);
      return { brandId, brandName: brand?.brand_name ?? null };
    });
    // "By Report" options are filled from fetcherConfigData
    // (RUN_PROCESS_FETCHER_CONFIG_QUERY_ID), not the Brands query.
    openRunBrandModal(oauthTokenHeader, selectedBrandData);
  };

  return (
    // Single row, never wraps: the filter buttons scroll horizontally in the
    // space left between the search box and Run Processes, so adding a filter
    // can't push Run Processes onto a new line.
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: "100%" }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <BrandDirectorySearch
            rows={rows}
            value={selectedBrandNames}
            onChange={setSelectedBrandNames}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            scrollbarWidth: "thin",
            // Compact, single-line filter buttons — scoped to this bar only;
            // the shared FilterComponent itself is untouched.
            "& .MuiButton-root": {
              flexShrink: 0,
              whiteSpace: "nowrap",
              minWidth: "auto",
              px: 0.75,
            },
          }}
        >
          <BrandsFilterItems rows={rows} {...filterProps} />
        </Box>
      </Stack>
      <Button
        variant="contained"
        color="secondary"
        endIcon={<PlayCircleIcon />}
        data-tracker="787"
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        disabled={
          selectedRows.length === 0 ||
          !hasUpdateBrandDataPermission ||
          hasNonRunnableSelection
        }
        onClick={handleRunProcesses}
      >
        Run Processes
      </Button>
    </Stack>
  );
};

export default AllBrandsFilterCard;
